import {ApolloClient, fromPromise, HttpLink, InMemoryCache} from "@apollo/client";
import {onError} from "@apollo/client/link/error";
import {setContext} from "@apollo/client/link/context";

const graphQlLocal = "http://localhost:4000";

const isLocalHost = window.location.hostname === "localhost";

// Note the "rewrites" in firebase.json which redirects all calls from /api/graphql to the graphql function
export const firebaseFunctionsUri =
    isLocalHost ? graphQlLocal
        : "/api";

export const firebaseGraphQlUri = firebaseFunctionsUri + "/graphql"


type TokenProvider = () => Promise<string | undefined>;
type AuthErrorHandler = () => void;

const apolloLink = new HttpLink({
    uri: firebaseGraphQlUri,
    credentials: "include"
});

const isUnauthorizedError = (errorMessage?: string) => {
    if (!errorMessage) {
        return false;
    }
    return errorMessage.toLowerCase().includes("unauthorized");
};
const tokenLength = (token?: string) => token?.length || 0;
const shouldTraceAuthOperation = (operationName?: string) => operationName === "GetUser";
const getTraceId = (headers: any) => headers?.["x-auth-trace-id"] || headers?.["X-Auth-Trace-Id"];

type NetworkErrorHandler = () => void;

export const createApolloClient = (
    getTokenAsync: TokenProvider,
    refreshToken: TokenProvider,
    onAuthError?: AuthErrorHandler,
    onNetworkError?: NetworkErrorHandler
) => {
    const authLink = setContext(async (operation, {headers}) => {
        const token = await getTokenAsync();
        if (shouldTraceAuthOperation(operation.operationName)) {
            console.log("[auth-trace] Apollo authLink", {
                operationName: operation.operationName,
                traceId: getTraceId(headers),
                hasToken: !!token,
                tokenLength: tokenLength(token)
            });
        }
        if (!token) {
            return {headers};
        }
        return {
            headers: {
                ...headers,
                authorization: token
            }
        };
    });

    const errorLink = onError(({graphQLErrors, networkError, operation, forward}) => {
        if ((graphQLErrors && graphQLErrors.length > 0) || networkError) {
            console.warn("[auth-trace] Apollo operation error", {
                operationName: operation.operationName,
                graphQLErrors: graphQLErrors?.map((err) => err.message) || [],
                networkError: networkError ? String(networkError) : undefined
            });
        }

        const hasGraphQlAuthError = graphQLErrors?.some((err) => isUnauthorizedError(err.message));
        const networkStatus = (networkError as {statusCode?: number} | undefined)?.statusCode;
        const hasNetworkAuthError = networkStatus === 401;

        if (networkError && !networkStatus) {
            onNetworkError?.();
            return;
        }

        if (!hasGraphQlAuthError && !hasNetworkAuthError) {
            return;
        }

        const {authRetry} = operation.getContext();
        if (authRetry) {
            console.warn("[auth-trace] Auth retry already attempted", {
                operationName: operation.operationName
            });
            return;
        }

        return fromPromise(refreshToken()).flatMap((token) => {
            console.warn("[auth-trace] Retrying operation after auth error", {
                operationName: operation.operationName,
                hasRefreshedToken: !!token,
                refreshedTokenLength: tokenLength(token)
            });
            if (!token) {
                onAuthError?.();
                operation.setContext(({headers = {}}) => ({
                    headers: {
                        ...headers
                    },
                    authRetry: true
                }));
                return forward(operation);
            }
            operation.setContext(({headers = {}}) => ({
                headers: {
                    ...headers,
                    authorization: token
                },
                authRetry: true
            }));
            return forward(operation);
        });
    });

    return new ApolloClient({
        // resolvers: localResolvers,
        link: errorLink.concat(authLink).concat(apolloLink),
        cache: new InMemoryCache({
            addTypename: false
        }),
        defaultOptions: {
            watchQuery: {
                fetchPolicy: "no-cache",
                errorPolicy: "ignore"
            },
            query: {
                fetchPolicy: "no-cache",
                errorPolicy: "all"
            }
        }
    });
}


